import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Button,
  Spinner,
  Card,
  Pagination,
  ButtonGroup,
} from "react-bootstrap";
import api from "../api";
import AccountingChart from "../components/AccountingChart";

function AccountingWalmart() {
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState("order");
  const [period, setPeriod] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [summary, setSummary] = useState({
    totalSales: 0,
    totalCost: 0,
    totalFees: 0,
    totalShipping: 0,
    totalProfit: 0,
    totalQuantity: 0,
    profitMargin: 0,
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  async function fetchProfit(
    newPage = 1,
    periodArg = period,
    startArg = startDate,
    endArg = endDate
  ) {
    setLoading(true);

    try {
      const query = new URLSearchParams({
        group,
        period: periodArg || "",
        start: startArg || "",
        end: endArg || "",
        page: newPage,
        limit: 50,
      }).toString();

      const res = await api.get(`/accounting/walmart/profit?${query}`);

      const rows = res.data.data || [];
      const chartRows = res.data.chart || [];

      setData(rows);
      setChartData(chartRows);
      setPage(res.data.currentPage || 1);
      setTotalPages(res.data.totalPages || 1);

      let totalSales = 0;
      let totalCost = 0;
      let totalFees = 0;
      let totalShipping = 0;
      let totalProfit = 0;
      let totalQuantity = 0;

      rows.forEach((row) => {
        totalSales += parseFloat(row.total_sales || 0);
        totalCost += parseFloat(row.total_cost || 0);
        totalFees += parseFloat(row.platform_fees || 0);
        totalShipping += parseFloat(row.shipping_cost || 0);
        totalProfit += parseFloat(row.total_profit || 0);
        totalQuantity += parseInt(row.quantity || 0, 10);
      });

      const profitMargin =
        totalSales > 0
          ? ((totalProfit / totalSales) * 100).toFixed(2)
          : "0.00";

          const roi =
  totalCost > 0
    ? ((totalProfit / totalCost) * 100).toFixed(2)
    : "0.00";

      setSummary({
  totalSales,
  totalCost,
  totalFees,
  totalShipping,
  totalProfit,
  totalQuantity,
  profitMargin,
  roi,
});
    } catch (err) {
      console.error("Walmart accounting error:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProfit(1);
  }, [group, period, startDate, endDate]);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    for (let p = 1; p <= totalPages; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === page}
          onClick={() => fetchProfit(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination>{items}</Pagination>
      </div>
    );
  };

  const safeMargin = (profit, sales) => {
    const s = parseFloat(sales || 0);
    const p = parseFloat(profit || 0);
    if (s <= 0) return "0.00";
    return ((p / s) * 100).toFixed(2);
  };

  return (
    <Container fluid className="p-4">
      <h3 className="mb-3">🏬 Walmart Accounting Dashboard</h3>

      {/* Filters */}
      <Row className="mb-3 align-items-center">
        <Col md={3}>
          <Form.Select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="sku">Group by SKU</option>
            <option value="order">Group by Order</option>
          </Form.Select>
        </Col>

        <Col md={6}>
          <ButtonGroup>
            <Button
              variant={period === "all" ? "primary" : "outline-dark"}
              onClick={() => setPeriod("all")}
            >
              All Time
            </Button>
            <Button
              variant={period === "day" ? "primary" : "outline-primary"}
              onClick={() => setPeriod("day")}
            >
              Today
            </Button>
            <Button
              variant={period === "week" ? "primary" : "outline-primary"}
              onClick={() => setPeriod("week")}
            >
              This Week
            </Button>
            <Button
              variant={period === "month" ? "primary" : "outline-primary"}
              onClick={() => setPeriod("month")}
            >
              This Month
            </Button>
            <Button
              variant={period === "custom" ? "primary" : "outline-secondary"}
              onClick={() => setPeriod("custom")}
            >
              Custom
            </Button>
          </ButtonGroup>
        </Col>

        {period === "custom" && (
          <>
            <Col md={2}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
          </>
        )}
      </Row>

      {/* Summary */}
      <Row className="mb-4">
  <Col md={3}>
    <Card className="text-center p-3 border-success">
      <h5>Total Sales</h5>
      <h4>${summary.totalSales.toFixed(2)}</h4>
    </Card>
  </Col>

  <Col md={3}>
    <Card className="text-center p-3 border-warning">
      <h5>Total COGS</h5>
      <h4>${summary.totalCost.toFixed(2)}</h4>
    </Card>
  </Col>

  <Col md={3}>
    <Card className="text-center p-3 border-info">
      <h5>Total Shipping</h5>
      <h4>${summary.totalShipping.toFixed(2)}</h4>
    </Card>
  </Col>

  <Col md={3}>
    <Card className="text-center p-3 border-danger">
      <h5>Total Fees</h5>
      <h4>${summary.totalFees.toFixed(2)}</h4>
    </Card>
  </Col>

  <Col md={3}>
    <Card className="text-center p-3 border-dark">
      <h5>Net Profit</h5>
      <h4>${summary.totalProfit.toFixed(2)}</h4>
    </Card>
  </Col>

  <Col md={3}>
  <Card className="text-center p-3 border-secondary">
    <h5>ROI</h5>
    <h4>{summary.roi || "0.00"}%</h4>
  </Card>
</Col>
</Row>

      <Row className="mb-4">
        <Col>
          <AccountingChart chartData={chartData} period={period} />
        </Col>
      </Row>

      {/* Table */}
      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>Image</th>
            <th>SKU</th>
            <th>{group === "sku" ? "Grouped SKU" : "Order ID"}</th>
            <th>Sales</th>
            <th>COGS</th>
            <th>Fees</th>
            <th>Shipping</th>
            <th>Profit</th>
            <th>Margin %</th>
            <th>ROI %</th>
            <th>Qty</th>
            <th>Last Order</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan="11" className="text-center">
                <Spinner animation="border" />
              </td>
            </tr>
          )}

          {!loading && data.length === 0 && (
            <tr>
              <td colSpan="11" className="text-center">
                No Walmart orders found.
              </td>
            </tr>
          )}

          {!loading &&
            data.map((row, i) => (
              <tr key={i}>
                <td>
                  {row.image_url ? (
                    <img
                      src={row.image_url}
                      alt="Product"
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "contain",
                        borderRadius: "6px",
                        border: "1px solid #ddd",
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </td>

                <td>{row.sku || "-"}</td>
                <td>{row.group_key}</td>
                <td>${parseFloat(row.total_sales || 0).toFixed(2)}</td>
                <td>${parseFloat(row.total_cost || 0).toFixed(2)}</td>
                <td>${parseFloat(row.platform_fees || 0).toFixed(2)}</td>
                <td>${parseFloat(row.shipping_cost || 0).toFixed(2)}</td>
                <td>
                  <strong
                    style={{
                      color:
                        parseFloat(row.total_profit || 0) >= 0
                          ? "#198754"
                          : "#dc3545",
                    }}
                  >
                    ${parseFloat(row.total_profit || 0).toFixed(2)}
                  </strong>
                </td>
                <td>
                  {safeMargin(row.total_profit, row.total_sales)}%
                </td>
                <td>
  {parseFloat(row.total_cost || 0) > 0
    ? ((parseFloat(row.total_profit || 0) /
        parseFloat(row.total_cost || 0)) *
        100).toFixed(2)
    : "0.00"}%
</td>
                <td>{row.quantity}</td>
                <td>{row.last_order_date || "-"}</td>
              </tr>
            ))}
        </tbody>
      </Table>

      {renderPagination()}
    </Container>
  );
}

export default AccountingWalmart;
